// Plan Management — Registers. Four evidence registers that answer "prove it": the fee audit
// register (spec 11), the statement log and who is still owed one (spec 12), the payment-batch
// register (spec 10) and the role matrix (spec 14).
//
// These are not dashboards. A dashboard shows how the month went; a register is scanned to find
// the ONE row that is wrong. So every panel is tuned so the eye lands on the exception — the
// month with no fee, the participant with no statement, the pay run still sitting at the bank,
// the ABA file whose hash no longer matches — and healthy rows recede.
//
// House rules from ui.jsx: colour encodes STATE only (primary is selection), money is tabular and
// right-aligned via the kit, real loading/empty/error states, no decorative colour.
import React, { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import {
  AlertOctagon, AlertTriangle, Ban, Banknote, Check, CheckCircle2, FileText, Fingerprint,
  Download, Grid3x3, Inbox, Info, Landmark, Lock, Mail, MailX, Printer, Receipt, RefreshCw, ShieldCheck,
  Users,
} from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import {
  CopyButton, Currency, DataTable, DetailDrawer, EmptyState, ErrorState, FilterChips, KpiTile,
  MicroLabel, SectionCard, StatusBadge, TableSkeleton, Toolbar, fmtDate, fmtDateTime, money,
} from './ui';

/* ── shared helpers ──────────────────────────────────────────────────────── */

// The API is the source of the message wherever it bothered to write one (403s especially —
// spec 14's "only an administrator can change plan-management roles" is worth reading verbatim).
const errMsg = (e, fallback) => (e && e.response && e.response.data && e.response.data.message) || fallback;
const httpStatus = (e) => (e && e.response && e.response.status) || 0;
const pretty = (s) => String(s || '').replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const fmtMonth = (m) => { const p = String(m || '').split('-'); const i = Number(p[1]) - 1; return MONTHS[i] ? `${MONTHS[i]} ${String(p[0]).slice(2)}` : String(m || '—'); };
const fmtMonthLong = (m) => { const p = String(m || '').split('-'); const i = Number(p[1]) - 1; return MONTHS[i] ? `${MONTHS[i]} ${p[0]}` : String(m || '—'); };
// Local, not UTC: toISOString() on a Sydney morning can roll the month backwards.
const ymLocal = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
const thisMonth = () => ymLocal(new Date());
const prevMonth = () => { const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() - 1); return ymLocal(d); };


// Export whatever the register is currently showing. None of the four registers could be exported
// at all: a fee register you cannot hand to an accountant, and a statement log you cannot attach to
// an audit response, are half a register.
const csvEscape = (v) => {
  const t = v == null ? '' : String(v);
  return /[",\r\n]/.test(t) ? '"' + t.replace(/"/g, '""') + '"' : t;
};
function exportRows(filename, columns, rows) {
  if (!rows || !rows.length) return;
  const head = columns.map((c) => csvEscape(c.header)).join(',');
  const body = rows.map((r) => columns.map((c) => csvEscape(typeof c.value === 'function' ? c.value(r) : r[c.value])).join(','));
  const url = URL.createObjectURL(new Blob([[head, ...body].join('\r\n')], { type: 'text/csv' }));
  const a = document.createElement('a'); a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
}
function ExportButton({ filename, columns, rows, label = 'Export CSV' }) {
  const n = (rows || []).length;
  return (
    <button type="button" onClick={() => exportRows(filename, columns, rows)} disabled={!n}
      title={n ? `Download these ${n} row${n === 1 ? '' : 's'} as CSV` : 'Nothing to export yet'}
      className="btn-secondary btn-sm disabled:opacity-40 inline-flex items-center gap-1.5">
      <Download size={12} />{label}
    </button>
  );
}

// One control style for the month pickers and role selects, matched to ui.jsx's SearchInput.
const CTRL = 'h-8 px-2 bg-white border border-slate-200 rounded-lg text-[12.5px] text-slate-800 '
  + 'hover:border-slate-300 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100 '
  + 'transition-colors motion-reduce:transition-none disabled:opacity-50 disabled:cursor-not-allowed';

// Small labelled control wrapper — keeps the toolbars from turning into naked inputs.
function ControlLabel({ label, children }) {
  return (
    <label className="flex items-center gap-1.5">
      <MicroLabel as="span">{label}</MicroLabel>
      {children}
    </label>
  );
}

// The API writes its own explanatory `note` on several of these endpoints. Surfacing it beats
// paraphrasing it in the client, where it would drift.
function NoteLine({ children, icon: Icon = Info, className }) {
  if (!children) return null;
  return (
    <p className={clsx('flex items-start gap-1.5 text-[11.5px] text-slate-500', className)}>
      <Icon size={12} className="mt-0.5 shrink-0 text-slate-300" />
      <span>{children}</span>
    </p>
  );
}

// Each panel keeps its own data/loading/error/nonce state inline rather than sharing a custom
// hook. A hook would read tidier, but destructured setters lose their known-stable identity and
// every effect below would then need a dependency-array escape hatch.

/* ══════════════════════════════════════════════════════════════════════════
   1 · FEE AUDIT REGISTER (spec 11)
   ══════════════════════════════════════════════════════════════════════════ */

// Cell styling per state. Two rose states, told apart by fill: FILLED rose = no fee was ever
// generated (the missed revenue this screen exists to find); OUTLINED rose = a fee that was
// claimed and rejected. `out_of_scope` gets no treatment at all — the participant simply was not
// with the plan manager that month, and painting it would compete with the real exceptions.
const FEE_STATE = {
  not_generated: {
    label: 'Missing', dot: 'bg-rose-500',
    cell: 'bg-rose-100 text-rose-700 ring-1 ring-inset ring-rose-300 font-semibold',
    legend: 'No fee was ever raised — revenue not claimed',
  },
  claim_error: {
    label: 'Claim error', dot: 'bg-white ring-1 ring-rose-400',
    cell: 'bg-white text-rose-700 ring-1 ring-inset ring-rose-300',
    legend: 'The NDIA rejected the fee claim',
  },
  generated: {
    label: 'Generated', dot: 'bg-amber-500',
    cell: 'bg-amber-50 text-amber-800 ring-1 ring-inset ring-amber-200',
    legend: 'Fee raised but not yet claimed',
  },
  claimed: {
    label: 'Claimed', dot: 'bg-cyan-500',
    cell: 'bg-cyan-50 text-cyan-700 ring-1 ring-inset ring-cyan-200',
    legend: 'Claim submitted to the NDIA',
  },
  paid_to_plan_manager: {
    label: 'Paid', dot: 'bg-emerald-500',
    cell: 'bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-200',
    legend: 'Reconciled — the fee was received',
  },
  cancelled: {
    label: 'Cancelled', dot: 'bg-slate-400',
    cell: 'bg-slate-50 text-slate-400 ring-1 ring-inset ring-slate-200 line-through',
    legend: 'Fee cancelled',
  },
  out_of_scope: {
    label: 'Not with you', dot: 'bg-white ring-1 ring-slate-200',
    cell: '', legend: 'Not with the plan manager that month — nothing was due',
  },
};
const LEGEND_ORDER = ['not_generated', 'claim_error', 'generated', 'claimed', 'paid_to_plan_manager', 'cancelled', 'out_of_scope'];

function FeeLegend() {
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 px-4 py-2.5 border-t border-slate-100 bg-slate-50/60">
      {LEGEND_ORDER.map((k) => (
        <span key={k} className="inline-flex items-center gap-1.5 text-[11px] text-slate-500">
          <span className={clsx('w-2.5 h-2.5 rounded-sm shrink-0', FEE_STATE[k].dot)} aria-hidden />
          <span className="font-medium text-slate-600">{FEE_STATE[k].label}</span>
          <span className="text-slate-400 hidden xl:inline">— {FEE_STATE[k].legend}</span>
        </span>
      ))}
    </div>
  );
}

function FeeGrid({ months, rows }) {
  // Per-month missing totals: the fastest read on the whole screen is "March lost 12 fees".
  const missingByMonth = useMemo(() => {
    const out = {};
    months.forEach((m) => { out[m] = 0; });
    rows.forEach((r) => (r.cells || []).forEach((c) => { if (c.state === 'not_generated') out[c.month] = (out[c.month] || 0) + 1; }));
    return out;
  }, [months, rows]);

  return (
    <div className="overflow-auto overscroll-contain max-h-[60vh]">
      <table className="border-separate border-spacing-0 text-[13px]">
        <caption className="sr-only">Monthly plan-management fee by participant</caption>
        <thead>
          <tr>
            <th scope="col"
              className="sticky top-0 left-0 z-30 bg-white border-b border-r border-slate-200 text-left w-[232px] min-w-[232px] px-3 py-2">
              <MicroLabel>Participant</MicroLabel>
            </th>
            {months.map((m) => (
              <th key={m} scope="col"
                className="sticky top-0 z-20 bg-white border-b border-slate-200 w-[66px] min-w-[66px] px-1 py-2 text-center">
                <span className="block text-[10.5px] font-semibold uppercase tracking-[0.04em] text-slate-400 whitespace-nowrap">{fmtMonth(m)}</span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const gaps = (r.cells || []).filter((c) => c.state === 'not_generated').length;
            return (
              <tr key={r.client_id} className="group">
                <th scope="row"
                  className={clsx('sticky left-0 z-10 border-b border-r border-slate-200 px-3 py-0.5 text-left font-normal',
                    // The row accent is the only place the missing-fee signal reaches the frozen
                    // column, so a gap is still visible when the grid is scrolled right.
                    gaps ? 'bg-rose-50/60 border-l-2 border-l-rose-400' : 'bg-white group-hover:bg-slate-50')}>
                  <span className="flex items-center gap-1.5 min-w-0">
                    <span className="truncate text-[12.5px] text-slate-800" title={`${r.participant}${r.ndis_number ? ` · ${r.ndis_number}` : ''}`}>
                      {r.participant}
                    </span>
                    {r.is_active === false && <span className="badge badge-gray !text-[9.5px] !px-1.5 !py-0 shrink-0">Inactive</span>}
                    {gaps > 0 && (
                      <span className="ml-auto shrink-0 tabular-nums text-[10.5px] font-bold text-rose-600" title={`${gaps} month${gaps === 1 ? '' : 's'} with no fee`}>
                        {gaps}
                      </span>
                    )}
                  </span>
                </th>
                {(r.cells || []).map((c) => {
                  const s = FEE_STATE[c.state] || FEE_STATE.out_of_scope;
                  const amount = c.amount == null ? null : Number(c.amount);
                  const title = `${r.participant} · ${fmtMonthLong(c.month)} — ${s.label}${amount != null ? ` ${money(amount)}` : ''}`;
                  return (
                    <td key={c.month} className={clsx('border-b border-slate-100 p-0.5 align-middle', gaps && 'bg-rose-50/25')}>
                      <span title={title}
                        className={clsx('flex h-6 items-center justify-center rounded-md px-1 tabular-nums text-[10.5px] leading-none truncate', s.cell)}>
                        {c.state === 'out_of_scope' ? '' : (amount != null ? money(amount) : s.label)}
                      </span>
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr>
            <th scope="row" className="sticky left-0 bottom-0 z-20 bg-slate-50 border-t border-r border-slate-200 px-3 py-1.5 text-left">
              <MicroLabel>Missing / month</MicroLabel>
            </th>
            {months.map((m) => (
              <td key={m} className="sticky bottom-0 z-10 bg-slate-50 border-t border-slate-200 px-1 py-1.5 text-center">
                <span className={clsx('tabular-nums text-[11px]', missingByMonth[m] ? 'font-bold text-rose-600' : 'text-slate-300')}>
                  {missingByMonth[m] || '—'}
                </span>
              </td>
            ))}
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

export function FeeRegister() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [nonce, setNonce] = useState(0);
  const reload = () => setNonce((n) => n + 1);
  const [months, setMonths] = useState(12);
  const [to, setTo] = useState(thisMonth);
  const [search, setSearch] = useState('');
  const [gapsOnly, setGapsOnly] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true); setError(null);
    api.get('/plan-management/fees/register', { params: { months, to } })
      .then((r) => { if (alive) setData(r.data); })
      .catch((e) => { if (alive) setError(errMsg(e, 'Could not load the fee register.')); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [months, to, nonce]);

  // Memoised so the fallback `[]` does not become a new array on every render and re-run the
  // filters (and, through them, the whole grid) for nothing.
  const grid = useMemo(() => (data && data.grid) || [], [data]);
  const monthList = useMemo(() => (data && data.months) || [], [data]);
  const exceptions = useMemo(() => (data && data.exceptions) || [], [data]);
  const gapRows = useMemo(() => grid.filter((r) => (r.cells || []).some((c) => c.state === 'not_generated')), [grid]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    let out = gapsOnly ? gapRows : grid;
    if (q) out = out.filter((r) => `${r.participant || ''} ${r.ndis_number || ''}`.toLowerCase().includes(q));
    return out;
  }, [grid, gapRows, gapsOnly, search]);

  const exceptionsValue = exceptions.length;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiTile label="Participants" value={loading ? '—' : (data ? data.participants : 0)} icon={Users} accent="slate"
          sub={`${monthList.length || months} months to ${fmtMonthLong(to)}`} />
        <KpiTile label="Fee months claimed" value={loading ? '—' : (data ? data.claimed : 0)} icon={CheckCircle2} accent="emerald"
          sub="Claimed or paid to the plan manager" />
        <KpiTile label="Claimed value" value={loading ? '—' : money(data ? data.claimed_value : 0)} icon={Banknote} accent="cyan"
          sub="Across the months shown" />
        <KpiTile label="Missed fee months" value={loading ? '—' : exceptionsValue} icon={AlertTriangle}
          alert={exceptionsValue > 0} accent="rose"
          sub={exceptionsValue > 0 ? 'In scope, but no fee was raised' : 'Every in-scope month has a fee'} />
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <Toolbar
          sticky={false} title="Fee audit register" icon={Grid3x3}
          count={loading ? undefined : rows.length} countLabel={rows.length === 1 ? 'participant' : 'participants'}
          search={search} onSearch={setSearch} searchPlaceholder="Participant or NDIS number"
          chips={(
            <FilterChips
              multi={false} ariaLabel="Months shown" value={months}
              onChange={(v) => { if (v) setMonths(v); }}
              options={[{ value: 6, label: '6 months' }, { value: 12, label: '12 months' }, { value: 24, label: '24 months' }]}
            />
          )}
          actions={(
            <>
              <ControlLabel label="Up to">
                <input type="month" value={to} max={thisMonth()} aria-label="Last month shown"
                  onChange={(e) => setTo(e.target.value || thisMonth())} className={clsx(CTRL, 'w-[9.5rem]')} />
              </ControlLabel>
              <ExportButton filename={`pm-fee-register-${new Date().toISOString().slice(0, 10)}.csv`} rows={rows}
                columns={[
                  { header: 'Participant', value: 'participant' },
                  { header: 'NDIS number', value: 'ndis_number' },
                  { header: 'Month', value: 'month' },
                  { header: 'Fee', value: 'amount' },
                  { header: 'Status', value: 'status' },
                ]} />
              <button type="button" onClick={reload} className="btn-secondary btn-sm" disabled={loading}>
                <RefreshCw size={12} className={clsx(loading && 'animate-spin')} />Refresh
              </button>
            </>
          )}
        >
          <FilterChips
            multi={false} ariaLabel="Show only rows with a missing fee" value={gapsOnly ? 'gaps' : null}
            onChange={(v) => setGapsOnly(v === 'gaps')}
            options={[{ value: 'gaps', label: 'Only rows with a gap', tone: 'rose', count: gapRows.length }]}
          />
        </Toolbar>

        {loading ? <TableSkeleton rows={10} cols={7} />
          : error ? <ErrorState message={error} onRetry={reload} />
            : !grid.length ? (
              <EmptyState icon={Users} title="No plan-managed participants"
                hint="The fee register lists every participant whose client type is plan managed. Add one and their monthly fees appear here." />
            ) : !rows.length ? (
              <EmptyState icon={CheckCircle2} title={gapsOnly ? 'No gaps in this window' : 'No participant matches that search'}
                hint={gapsOnly ? 'Every in-scope month across these participants has a fee raised against it.' : 'Clear the search to see the whole register.'} />
            ) : (
              <>
                <FeeGrid months={monthList} rows={rows} />
                <FeeLegend />
              </>
            )}
      </div>

      <SectionCard
        title="Exceptions — participant-months with no fee" icon={AlertTriangle}
        actions={exceptions.length > 0 ? (
          <span className="text-[11.5px] font-semibold text-rose-600 tabular-nums">{exceptions.length} to investigate</span>
        ) : null}
      >
        <DataTable
          dense maxHeight="22rem" label="Fee exceptions"
          rows={exceptions} loading={loading} error={error} onRetry={reload}
          rowKey={(r, i) => `${r.client_id}|${r.month}|${i}`}
          empty={{
            icon: CheckCircle2,
            title: 'Nothing outstanding',
            hint: 'Every month a participant was with you, and their plan was live, has a fee raised against it.',
          }}
          columns={[
            { key: 'participant', header: 'Participant', sortable: true, truncate: true },
            { key: 'month', header: 'Month', width: 120, sortable: true, render: (r) => <span className="tabular-nums text-slate-600">{fmtMonthLong(r.month)}</span> },
            { key: 'reason', header: 'Reason', render: (r) => <span className="text-slate-500">{r.reason}</span> },
          ]}
          footer={exceptions.length ? `${exceptions.length} participant-month${exceptions.length === 1 ? '' : 's'} with no fee raised — revenue that is otherwise invisible, because a missing row looks like nothing at all.` : undefined}
        />
        {data && data.note ? <div className="px-4 py-2.5 border-t border-slate-100"><NoteLine>{data.note}</NoteLine></div> : null}
      </SectionCard>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   2 · STATEMENTS (spec 12)
   ══════════════════════════════════════════════════════════════════════════ */

const STMT_STATE = {
  generated: { label: 'Awaiting send', badge: 'badge-yellow', tone: 'amber' },
  sent: { label: 'Sent', badge: 'badge-green', tone: null },
  printed: { label: 'Printed & posted', badge: 'badge-green', tone: null },
  bounced: { label: 'Bounced', badge: 'badge-red', tone: 'rose' },
  failed: { label: 'Failed', badge: 'badge-red', tone: 'rose' },
};
const stmtState = (s) => STMT_STATE[s] || { label: pretty(s), badge: 'badge-gray', tone: null };

function DeliveryChip({ delivery }) {
  const d = String(delivery || 'email');
  if (d === 'paper') {
    return <span className="badge badge-yellow !text-[10.5px]"><Printer size={10} />Paper</span>;
  }
  return <span className="badge badge-gray !text-[10.5px]">{pretty(d)}</span>;
}

export function StatementRegister() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [nonce, setNonce] = useState(0);
  const reload = () => setNonce((n) => n + 1);
  const [due, setDue] = useState(null);
  const [dueLoading, setDueLoading] = useState(true);
  const [dueError, setDueError] = useState(null);
  const [period, setPeriod] = useState(prevMonth);
  const [statusFilter, setStatusFilter] = useState(null);
  const [search, setSearch] = useState('');
  const [printingId, setPrintingId] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true); setError(null);
    api.get('/plan-management/statements/log', { params: { period } })
      .then((r) => { if (alive) setData(r.data); })
      .catch((e) => { if (alive) setError(errMsg(e, 'Could not load the statement log.')); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [period, nonce]);

  useEffect(() => {
    let alive = true;
    setDueLoading(true); setDueError(null);
    api.get('/plan-management/statements/due', { params: { period } })
      .then((r) => { if (alive) setDue(r.data); })
      .catch((e) => { if (alive) setDueError(errMsg(e, 'Could not work out who is still due a statement.')); })
      .finally(() => { if (alive) setDueLoading(false); });
    return () => { alive = false; };
  }, [period, nonce]);

  const summary = (data && data.summary) || {};
  const statements = useMemo(() => (data && data.statements) || [], [data]);

  // Paper statements that exist but have not been printed. This is the actionable list: the
  // "still due" endpoint returns participants with NO statement row at all, so those have no id
  // to mark printed — they have to be generated first.
  const toPrint = useMemo(
    () => statements.filter((s) => String(s.delivery) === 'paper' && ['generated', 'bounced'].includes(String(s.status))),
    [statements],
  );

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    let out = statements;
    if (statusFilter) out = out.filter((s) => String(s.status) === statusFilter);
    if (q) out = out.filter((s) => `${s.participant || ''} ${s.ndis_number || ''} ${s.sent_to || ''}`.toLowerCase().includes(q));
    return out;
  }, [statements, statusFilter, search]);

  const markPrinted = async (row) => {
    setPrintingId(row.id);
    try {
      const { data: updated } = await api.patch(`/plan-management/statements/${row.id}/printed`);
      // Patch in place rather than refetching: the operator is working down a printed pile and
      // must not lose their position in the list.
      setData((d) => (d ? {
        ...d,
        statements: (d.statements || []).map((s) => (s.id === row.id ? { ...s, ...updated } : s)),
        summary: { ...(d.summary || {}), printed: Number((d.summary || {}).printed || 0) + 1, awaiting: Math.max(0, Number((d.summary || {}).awaiting || 0) - 1) },
      } : d));
      toast.success(`Statement for ${row.participant} marked printed and posted`);
    } catch (e) {
      toast.error(errMsg(e, 'Could not mark that statement printed.'));
    } finally { setPrintingId(null); }
  };

  const dueCount = due ? due.count : 0;
  const duePaper = (due && due.paper) || [];

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 flex-wrap">
        <ControlLabel label="Statement period">
          <input type="month" value={period} max={thisMonth()} aria-label="Statement period"
            onChange={(e) => setPeriod(e.target.value || prevMonth())} className={clsx(CTRL, 'w-[9.5rem]')} />
        </ControlLabel>
        <span className="text-[11.5px] text-slate-400">{fmtMonthLong(period)}</span>
        <button type="button" onClick={reload} className="btn-secondary btn-sm ml-auto" disabled={loading || dueLoading}>
          <RefreshCw size={12} className={clsx((loading || dueLoading) && 'animate-spin')} />Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {/* The missing statement leads, not the sent count: the NDIA expects one per participant
            per month, so the absence is the compliance fact. */}
        <KpiTile label="No statement yet" value={dueLoading ? '—' : dueCount} icon={MailX}
          alert={dueCount > 0} accent="rose"
          sub={dueCount > 0 ? `${fmtMonthLong(period)} — one is required per participant` : 'Every active participant has one'} />
        <KpiTile label="Paper still to print" value={loading ? '—' : toPrint.length} icon={Printer}
          accent={toPrint.length ? 'amber' : 'slate'}
          sub={toPrint.length ? 'Generated, not yet printed and posted' : 'Nothing waiting at the printer'} />
        <KpiTile label="Issued" value={loading ? '—' : (Number(summary.sent || 0) + Number(summary.printed || 0))} icon={Mail}
          accent="emerald" sub={`${Number(summary.sent || 0)} sent · ${Number(summary.printed || 0)} printed`} />
        <KpiTile label="Bounced" value={loading ? '—' : Number(summary.bounced || 0)} icon={AlertOctagon}
          alert={Number(summary.bounced || 0) > 0} accent="rose"
          sub={Number(summary.bounced || 0) > 0 ? 'Delivery failed — the participant has not seen it' : 'No delivery failures'} />
      </div>

      {/* ── Who is still due ── */}
      <SectionCard title={`Still due — ${fmtMonthLong(period)}`} icon={MailX}
        actions={dueCount > 0 ? <span className="text-[11.5px] font-semibold text-rose-600 tabular-nums">{dueCount} participant{dueCount === 1 ? '' : 's'}</span> : null}>
        <DataTable
          dense maxHeight="20rem" label="Participants with no statement"
          rows={(due && due.participants) || []} loading={dueLoading} error={dueError} onRetry={reload}
          rowKey="client_id" rowTone={(r) => (String(r.delivery) === 'paper' ? 'amber' : 'rose')}
          empty={{
            icon: CheckCircle2,
            title: 'Nothing outstanding',
            hint: `Every active plan-managed participant has a statement for ${fmtMonthLong(period)}.`,
          }}
          columns={[
            { key: 'participant', header: 'Participant', sortable: true, truncate: true },
            { key: 'ndis_number', header: 'NDIS number', width: 150, render: (r) => <span className="tabular-nums text-slate-500">{r.ndis_number || '—'}</span> },
            { key: 'delivery', header: 'Delivery', width: 120, render: (r) => <DeliveryChip delivery={r.delivery} /> },
          ]}
          footer={dueCount ? `${dueCount} participant${dueCount === 1 ? '' : 's'} with no statement for ${fmtMonthLong(period)}${duePaper.length ? ` · ${duePaper.length} of them receive theirs on paper` : ''}. A statement has to be generated before it can be sent or printed.` : undefined}
        />
      </SectionCard>

      {/* ── Paper statement due (the printable task) ── */}
      <SectionCard title="Paper statement due" icon={Printer}
        actions={toPrint.length > 0 ? <span className="text-[11.5px] font-semibold text-amber-600 tabular-nums">{toPrint.length} to print</span> : null}>
        <DataTable
          dense label="Paper statements awaiting printing"
          rows={toPrint} loading={loading} error={error} onRetry={reload} rowTone={() => 'amber'}
          empty={{
            icon: Printer,
            title: 'Nothing waiting at the printer',
            hint: 'Paper-delivery statements that have been generated but not yet printed and posted appear here.',
          }}
          columns={[
            { key: 'participant', header: 'Participant', sortable: true, truncate: true },
            { key: 'period', header: 'Period', width: 120, render: (r) => <span className="tabular-nums text-slate-600">{fmtMonthLong(r.period)}</span> },
            { key: 'status', header: 'Status', width: 150, render: (r) => <span className={clsx('badge !text-[10.5px]', stmtState(r.status).badge)}>{stmtState(r.status).label}</span> },
            { key: 'created_at', header: 'Generated', width: 150, render: (r) => <span className="text-slate-500 tabular-nums">{fmtDate(r.created_at)}</span> },
          ]}
          rowActions={(r) => (
            <button type="button" className="btn-secondary btn-sm" disabled={printingId === r.id}
              onClick={(e) => { e.stopPropagation(); markPrinted(r); }}>
              <Printer size={12} />{printingId === r.id ? 'Saving…' : 'Mark printed'}
            </button>
          )}
          footer={toPrint.length ? 'Marking a statement printed records who printed it and when, so the monthly obligation can be evidenced.' : undefined}
        />
      </SectionCard>

      {/* ── The log ── */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <Toolbar
          sticky={false} title="Statement log" icon={FileText}
          count={loading ? undefined : rows.length} countLabel={rows.length === 1 ? 'statement' : 'statements'}
          search={search} onSearch={setSearch} searchPlaceholder="Participant, NDIS number or address"
          chips={(
            <FilterChips
              multi={false} ariaLabel="Filter by delivery status" value={statusFilter} onChange={setStatusFilter}
              options={[
                { value: null, label: 'All', count: Number(summary.total || 0) },
                { value: 'sent', label: 'Sent', tone: 'emerald', count: Number(summary.sent || 0) },
                { value: 'printed', label: 'Printed', tone: 'emerald', count: Number(summary.printed || 0) },
                { value: 'generated', label: 'Awaiting send', tone: 'amber', count: Number(summary.awaiting || 0) },
                { value: 'bounced', label: 'Bounced', tone: 'rose', count: Number(summary.bounced || 0) },
              ]}
            />
          )}
        />
        <div className="flex justify-end mb-2">
          <ExportButton filename={`pm-statements-${new Date().toISOString().slice(0, 10)}.csv`} rows={rows}
            columns={[
              { header: 'Participant', value: 'participant' },
              { header: 'Period', value: 'period' },
              { header: 'Status', value: 'status' },
              { header: 'Delivery', value: 'delivery' },
              { header: 'Sent to', value: 'sent_to' },
              { header: 'Sent at', value: 'sent_at' },
              { header: 'Printed at', value: 'printed_at' },
            ]} />
        </div>
        <DataTable
          dense maxHeight="30rem" label="Statement log"
          rows={rows} loading={loading} error={error} onRetry={reload}
          rowTone={(r) => stmtState(r.status).tone}
          empty={{
            icon: FileText,
            title: statusFilter || search ? 'No statement matches that filter' : `No statements recorded for ${fmtMonthLong(period)}`,
            hint: statusFilter || search ? 'Clear the filter to see the whole log.' : 'Once statements are generated for this period they are logged here with how and when each one went out.',
          }}
          columns={[
            {
              key: 'participant',
              header: 'Participant',
              sortable: true,
              render: (r) => (
                <span className="min-w-0 block">
                  <span className="block truncate text-slate-800">{r.participant}</span>
                  {r.ndis_number && <span className="block text-[11px] tabular-nums text-slate-400">{r.ndis_number}</span>}
                </span>
              ),
            },
            { key: 'period', header: 'Period', width: 110, sortable: true, render: (r) => <span className="tabular-nums text-slate-600">{fmtMonthLong(r.period)}</span> },
            { key: 'delivery', header: 'Delivery', width: 110, render: (r) => <DeliveryChip delivery={r.delivery} /> },
            { key: 'status', header: 'Status', width: 150, sortable: true, render: (r) => <span className={clsx('badge !text-[10.5px]', stmtState(r.status).badge)}>{stmtState(r.status).label}</span> },
            { key: 'sent_to', header: 'Sent to', truncate: true, render: (r) => <span className="text-slate-500">{r.sent_to || '—'}</span> },
            {
              key: 'sent_at',
              header: 'Issued',
              width: 165,
              sortable: true,
              sortValue: (r) => r.sent_at || r.printed_at || r.created_at,
              render: (r) => <span className="tabular-nums text-slate-500">{r.sent_at ? fmtDateTime(r.sent_at) : r.printed_at ? fmtDateTime(r.printed_at) : '—'}</span>,
            },
            {
              key: 'bounce_reason',
              header: 'Bounce reason',
              truncate: true,
              render: (r) => (r.bounce_reason
                ? <span className="text-rose-600" title={r.bounce_reason}>{r.bounce_reason}</span>
                : <span className="text-slate-300">—</span>),
            },
          ]}
        />
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   3 · PAYMENT BATCHES (spec 10)
   ══════════════════════════════════════════════════════════════════════════ */

const BATCH_STATE = {
  generated: { label: 'Awaiting release', badge: 'badge-yellow', tone: 'amber' },
  processed: { label: 'Processed', badge: 'badge-green', tone: null },
  voided: { label: 'Voided', badge: 'badge-gray', tone: 'slate' },
};
const batchState = (s) => BATCH_STATE[s] || { label: pretty(s), badge: 'badge-gray', tone: null };

// Spec 10.5 — the stored ABA file is hashed when the batch is generated and re-hashed on read. A
// mismatch means the file changed after it was signed off, which is exactly how money ends up in
// an account nobody authorised. This is not a badge; it is a stop sign.
function AbaIntegrity({ batch }) {
  const hasHash = !!batch.aba_hash;
  const valid = batch.hashValid !== false;
  if (hasHash && !valid) {
    return (
      <div className="rounded-xl border-2 border-rose-400 bg-rose-50 p-4 flex items-start gap-3">
        <AlertOctagon size={20} className="text-rose-600 shrink-0 mt-0.5" />
        <div className="min-w-0">
          <p className="text-[14px] font-bold text-rose-700">Do not release this batch</p>
          <p className="text-[12.5px] text-slate-700 mt-1">
            The stored ABA file no longer matches the SHA-256 recorded when the batch was generated, so the
            file has changed since it was approved. Void this pay run and generate a new one — uploading this
            file to the bank could pay accounts nobody authorised.
          </p>
          <p className="text-[11.5px] text-rose-700 font-semibold mt-2">
            The server will refuse to mark this batch processed while the hashes differ.
          </p>
        </div>
      </div>
    );
  }
  return (
    <div className={clsx('rounded-xl border px-4 py-3', hasHash ? 'border-emerald-200 bg-emerald-50/60' : 'border-slate-200 bg-slate-50/60')}>
      <div className="flex items-center gap-2">
        {hasHash ? <CheckCircle2 size={14} className="text-emerald-600 shrink-0" /> : <Fingerprint size={14} className="text-slate-400 shrink-0" />}
        <p className={clsx('text-[12.5px] font-semibold', hasHash ? 'text-emerald-700' : 'text-slate-600')}>
          {hasHash ? 'Stored ABA file matches its SHA-256' : 'No ABA file hash recorded for this batch'}
        </p>
        {hasHash && <CopyButton value={batch.aba_hash} label="Copy hash" className="ml-auto" />}
      </div>
      {hasHash && (
        <p className="mt-1.5 font-mono text-[10.5px] leading-relaxed text-slate-500 break-all">{batch.aba_hash}</p>
      )}
      {!hasHash && (
        <p className="text-[11.5px] text-slate-500 mt-1">Nothing to verify — this batch has no stored bank file.</p>
      )}
    </div>
  );
}

// Confirming the bank processed a pay run used to be possible only on the Pay providers screen,
// straight after Download ABA. Leave that screen and a generated batch could never be confirmed,
// so its bank reference was never recorded and no provider got a remittance advice. The register
// now offers the same confirmation, through the same endpoint, for any batch still awaiting release.
function ConfirmBatchProcessed({ batch, onDone }) {
  const [bankRef, setBankRef] = useState('');
  const [busy, setBusy] = useState(false);
  const hashBad = !!batch.aba_hash && batch.hashValid === false;
  const confirm = async () => {
    const ref = bankRef.trim();
    if (!ref) { toast.error("Enter the bank's batch reference"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/plan-management/payment-batches/${batch.id}/processed`, { bank_reference: ref });
      toast.success(`${batch.batch_ref || 'Batch'} confirmed as processed at the bank`);
      if (r.data && r.data.remitted) toast.success(`Remittance advice emailed to ${r.data.remitted} provider${r.data.remitted === 1 ? '' : 's'}`);
      setBankRef('');
      if (onDone) onDone();
    } catch (e) {
      toast.error(errMsg(e, 'Could not confirm this batch. Try again.'));
    } finally { setBusy(false); }
  };
  return (
    <div className="mt-3 flex flex-col sm:flex-row sm:items-end gap-2">
      <label className="flex-1 min-w-0">
        <MicroLabel as="span">Bank batch reference</MicroLabel>
        <input
          value={bankRef} onChange={(e) => setBankRef(e.target.value)} disabled={busy || hashBad}
          placeholder="Reference shown by your bank after release"
          className={clsx(CTRL, 'w-full mt-1')}
          onKeyDown={(e) => { if (e.key === 'Enter') confirm(); }}
        />
      </label>
      <button type="button" onClick={confirm} disabled={busy || hashBad || !bankRef.trim()} className="btn-primary btn-sm shrink-0"
        title={hashBad ? 'The stored ABA file no longer matches its hash, so this batch cannot be confirmed' : undefined}>
        <CheckCircle2 size={13} /> {busy ? 'Confirming…' : 'Confirm bank processed'}
      </button>
    </div>
  );
}

function BatchDetail({ batch, loading, error, onRetry, onClose, onChanged }) {
  if (loading) return <DetailDrawer open onClose={onClose} title="Payment batch" icon={Landmark}><TableSkeleton rows={6} cols={4} /></DetailDrawer>;
  if (error) return <DetailDrawer open onClose={onClose} title="Payment batch" icon={Landmark}><ErrorState message={error} onRetry={onRetry} /></DetailDrawer>;
  if (!batch) return null;

  const st = batchState(batch.status);
  const items = batch.items || [];
  const invoices = batch.invoices || [];

  return (
    <DetailDrawer
      open onClose={onClose} width="xl" icon={Landmark}
      title={batch.batch_ref || 'Payment batch'}
      subtitle={`${Number(batch.payment_count || 0)} payment${Number(batch.payment_count) === 1 ? '' : 's'} across ${Number(batch.invoice_count || 0)} invoice${Number(batch.invoice_count) === 1 ? '' : 's'}`}
      statusNode={<span className={clsx('badge', st.badge)}>{st.label}</span>}
      meta={[
        { label: 'Total', value: money(batch.total_amount), money: true },
        { label: 'Created', value: `${fmtDate(batch.created_at)}${batch.created_by_name ? ` · ${batch.created_by_name}` : ''}` },
        { label: 'Processed', value: batch.processed_at ? `${fmtDate(batch.processed_at)}${batch.processed_by_name ? ` · ${batch.processed_by_name}` : ''}` : null },
        { label: 'Bank reference', value: batch.bank_reference || null },
        { label: 'Remittance', value: batch.remittance_sent ? 'Sent to providers' : 'Not sent' },
      ]}
    >
      <div className="space-y-4">
        <AbaIntegrity batch={batch} />

        {batch.status === 'generated' && (
          <div className="rounded-xl border border-amber-200 bg-amber-50/70 px-4 py-3 flex items-start gap-2.5">
            <AlertTriangle size={14} className="text-amber-600 shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1">
              <p className="text-[12.5px] text-slate-700">
                <span className="font-semibold text-amber-700">The money has not moved.</span>{' '}
                This file has been generated but nobody has confirmed the bank processed it, so every provider
                in it is still waiting. Once the bank has released it, enter the bank's reference to confirm:
                it is recorded against every invoice in the run and each provider is emailed a remittance advice.
              </p>
              <ConfirmBatchProcessed batch={batch} onDone={onChanged} />
            </div>
          </div>
        )}

        {batch.status === 'voided' && (
          <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 flex items-start gap-2.5">
            <Ban size={14} className="text-slate-500 shrink-0 mt-0.5" />
            <p className="text-[12.5px] text-slate-600">
              <span className="font-semibold text-slate-700">Voided {batch.voided_at ? fmtDate(batch.voided_at) : ''}.</span>{' '}
              {batch.void_reason || 'No reason was recorded.'} The invoices were returned to the pay queue.
            </p>
          </div>
        )}

        <SectionCard title="Payments in this file" icon={Banknote}>
          <DataTable
            dense label="Payment lines" rows={items} rowKey="id"
            empty={{ icon: Banknote, title: 'No payment lines', hint: 'This batch carries no individual payments.' }}
            columns={[
              { key: 'provider_name', header: 'Payee', truncate: true, sortable: true },
              { key: 'account_name', header: 'Account name', truncate: true, render: (r) => <span className="text-slate-500">{r.account_name || '—'}</span> },
              { key: 'bsb', header: 'BSB', width: 90, render: (r) => <span className="tabular-nums text-slate-500">{r.bsb || '—'}</span> },
              { key: 'account_number', header: 'Account', width: 120, render: (r) => <span className="tabular-nums text-slate-500">{r.account_number || '—'}</span> },
              { key: 'amount', header: 'Amount', money: true, width: 110, sortable: true },
            ]}
            footer={items.length ? <span className="flex items-center justify-between"><span>{items.length} payment line{items.length === 1 ? '' : 's'}</span><Currency value={items.reduce((s, i) => s + Number(i.amount || 0), 0)} className="font-semibold" /></span> : undefined}
          />
        </SectionCard>

        <SectionCard title="Invoices settled by this batch" icon={Receipt}>
          <DataTable
            dense maxHeight="20rem" label="Invoices in batch" rows={invoices} rowKey="id"
            empty={{ icon: Receipt, title: 'No invoices attached', hint: 'Nothing is linked to this batch.' }}
            columns={[
              { key: 'provider_invoice_number', header: 'Invoice', width: 140, truncate: true, render: (r) => <span className="tabular-nums text-slate-700">{r.provider_invoice_number || '—'}</span> },
              { key: 'participant', header: 'Participant', truncate: true, sortable: true },
              { key: 'provider_name', header: 'Provider', truncate: true },
              { key: 'status', header: 'Status', width: 150, render: (r) => <StatusBadge status={r.status} /> },
              { key: 'total_amount', header: 'Amount', money: true, width: 110, sortable: true },
            ]}
          />
        </SectionCard>
      </div>
    </DetailDrawer>
  );
}

export function PaymentBatchRegister() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [nonce, setNonce] = useState(0);
  const reload = () => setNonce((n) => n + 1);
  const [statusFilter, setStatusFilter] = useState(null);
  const [search, setSearch] = useState('');
  const [openId, setOpenId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);
  const [detailNonce, setDetailNonce] = useState(0);

  useEffect(() => {
    let alive = true;
    setLoading(true); setError(null);
    api.get('/plan-management/payment-batches')
      .then((r) => { if (alive) setData(Array.isArray(r.data) ? r.data : []); })
      .catch((e) => { if (alive) setError(errMsg(e, 'Could not load the payment-batch register.')); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [nonce]);

  useEffect(() => {
    if (!openId) { setDetail(null); setDetailError(null); return undefined; }
    let alive = true;
    setDetailLoading(true); setDetailError(null); setDetail(null);
    api.get(`/plan-management/payment-batches/${openId}`)
      .then((r) => { if (alive) setDetail(r.data); })
      .catch((e) => { if (alive) setDetailError(errMsg(e, 'Could not open this payment batch.')); })
      .finally(() => { if (alive) setDetailLoading(false); });
    return () => { alive = false; };
  }, [openId, detailNonce]);

  const batches = useMemo(() => data || [], [data]);
  const counts = useMemo(() => batches.reduce((a, b) => {
    const k = String(b.status);
    a[k] = (a[k] || 0) + 1;
    if (k === 'generated') a.awaitingValue += Number(b.total_amount || 0);
    if (k === 'processed') a.processedValue += Number(b.total_amount || 0);
    return a;
  }, { awaitingValue: 0, processedValue: 0 }), [batches]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    let out = batches;
    if (statusFilter) out = out.filter((b) => String(b.status) === statusFilter);
    if (q) out = out.filter((b) => `${b.batch_ref || ''} ${b.bank_reference || ''} ${b.created_by_name || ''}`.toLowerCase().includes(q));
    return out;
  }, [batches, statusFilter, search]);

  const awaiting = counts.generated || 0;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {/* A generated batch is money that has NOT moved. It leads the register because it is the
            only state with an action outstanding at the bank. */}
        <KpiTile label="Awaiting release" value={loading ? '—' : awaiting} icon={AlertTriangle}
          alert={awaiting > 0} accent="amber"
          sub={awaiting > 0 ? 'Generated — not yet confirmed at the bank' : 'Nothing sitting unreleased'} />
        <KpiTile label="Value not yet moved" value={loading ? '—' : money(counts.awaitingValue)} icon={Banknote}
          alert={awaiting > 0} accent="amber" sub="Providers still waiting on this" />
        <KpiTile label="Processed" value={loading ? '—' : (counts.processed || 0)} icon={CheckCircle2} accent="emerald"
          sub={`${money(counts.processedValue)} paid out`} />
        <KpiTile label="Voided" value={loading ? '—' : (counts.voided || 0)} icon={Ban} accent="slate"
          sub={counts.voided ? 'Invoices returned to the pay queue' : 'No pay run has been voided'} />
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <Toolbar
          sticky={false} title="Payment batch register" icon={Landmark}
          count={loading ? undefined : rows.length} countLabel={rows.length === 1 ? 'pay run' : 'pay runs'}
          search={search} onSearch={setSearch} searchPlaceholder="Batch or bank reference"
          chips={(
            <FilterChips
              multi={false} ariaLabel="Filter by batch status" value={statusFilter} onChange={setStatusFilter}
              options={[
                { value: null, label: 'All', count: batches.length },
                { value: 'generated', label: 'Awaiting release', tone: 'amber', count: counts.generated || 0 },
                { value: 'processed', label: 'Processed', tone: 'emerald', count: counts.processed || 0 },
                { value: 'voided', label: 'Voided', tone: 'slate', count: counts.voided || 0 },
              ]}
            />
          )}
          actions={(
            <>
              <ExportButton filename={`pm-payment-batches-${new Date().toISOString().slice(0, 10)}.csv`} rows={rows}
                columns={[
                  { header: 'Batch', value: 'batch_ref' },
                  { header: 'Status', value: 'status' },
                  { header: 'Payments', value: 'payment_count' },
                  { header: 'Invoices', value: 'invoice_count' },
                  { header: 'Total', value: 'total_amount' },
                  { header: 'Bank reference', value: 'bank_reference' },
                  { header: 'Created', value: 'created_at' },
                  { header: 'Processed', value: 'processed_at' },
                ]} />
              <button type="button" onClick={reload} className="btn-secondary btn-sm" disabled={loading}>
                <RefreshCw size={12} className={clsx(loading && 'animate-spin')} />Refresh
              </button>
            </>
          )}
        />
        <DataTable
          dense maxHeight="34rem" label="Payment batches"
          rows={rows} loading={loading} error={error} onRetry={reload}
          onRowClick={(r) => setOpenId(r.id)} activeRowKey={openId}
          rowTone={(r) => batchState(r.status).tone}
          empty={{
            icon: Inbox,
            title: statusFilter || search ? 'No pay run matches that filter' : 'No pay runs yet',
            hint: statusFilter || search ? 'Clear the filter to see every pay run.' : 'Every ABA file generated for provider payments is registered here with its own SHA-256.',
          }}
          columns={[
            {
              key: 'batch_ref',
              header: 'Batch',
              sortable: true,
              render: (r) => (
                <span className="flex items-center gap-1 min-w-0">
                  <span className="truncate tabular-nums text-slate-800 font-medium">{r.batch_ref || '—'}</span>
                  {r.aba_hash && <Fingerprint size={11} className="shrink-0 text-slate-300" aria-label="ABA file hash stored" />}
                  {r.batch_ref && <CopyButton value={r.batch_ref} />}
                </span>
              ),
            },
            { key: 'status', header: 'Status', width: 160, sortable: true, render: (r) => <span className={clsx('badge !text-[10.5px]', batchState(r.status).badge)}>{batchState(r.status).label}</span> },
            { key: 'payment_count', header: 'Payments', width: 95, align: 'right', sortable: true, render: (r) => <span className="tabular-nums text-slate-600">{Number(r.payment_count || 0)}</span> },
            { key: 'invoice_count', header: 'Invoices', width: 95, align: 'right', sortable: true, render: (r) => <span className="tabular-nums text-slate-600">{Number(r.invoice_count || 0)}</span> },
            { key: 'total_amount', header: 'Total', money: true, width: 120, sortable: true },
            { key: 'bank_reference', header: 'Bank ref', width: 150, truncate: true, render: (r) => (r.bank_reference ? <span className="tabular-nums text-slate-600">{r.bank_reference}</span> : <span className="text-slate-300">—</span>) },
            {
              key: 'remittance_sent',
              header: 'Remittance',
              width: 110,
              render: (r) => (r.remittance_sent
                ? <span className="text-[11.5px] text-emerald-600">Sent</span>
                : <span className="text-[11.5px] text-slate-400">—</span>),
            },
            { key: 'created_at', header: 'Created', width: 155, sortable: true, defaultDir: 'desc', render: (r) => <span className="tabular-nums text-slate-500">{fmtDate(r.created_at)}</span> },
            { key: 'created_by_name', header: 'Created by', truncate: true, render: (r) => <span className="text-slate-500">{r.created_by_name || '—'}</span> },
          ]}
          footer={rows.length ? (
            <span className="flex items-center justify-between gap-3">
              <span>{rows.length} pay run{rows.length === 1 ? '' : 's'} shown{awaiting ? ` · ${awaiting} still awaiting release` : ''}</span>
              <Currency value={rows.reduce((s, b) => s + Number(b.total_amount || 0), 0)} className="font-semibold" />
            </span>
          ) : undefined}
        />
        <div className="px-4 py-2.5 border-t border-slate-100">
          <NoteLine icon={Fingerprint}>
            Open a pay run to verify its stored ABA file against the SHA-256 recorded when it was generated.
            A mismatch means the file changed after approval and the batch must not be released.
          </NoteLine>
        </div>
      </div>

      {openId && (
        <BatchDetail
          batch={detail} loading={detailLoading} error={detailError}
          onRetry={() => setDetailNonce((n) => n + 1)} onClose={() => setOpenId(null)}
          onChanged={() => { reload(); setDetailNonce((n) => n + 1); }}
        />
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   4 · ROLES (spec 14)
   ══════════════════════════════════════════════════════════════════════════ */

const ROLE_ORDER = ['administrator', 'plan_manager', 'finance', 'read_only'];
const ROLE_LABEL = { administrator: 'Administrator', plan_manager: 'Plan manager', finance: 'Finance', read_only: 'Read only' };
// The blurbs say what is deliberately WITHHELD — that is the whole point of a separation of
// duties, and a matrix of ticks does not say it out loud.
const ROLE_BLURB = {
  administrator: 'Everything, including handing out these roles.',
  plan_manager: 'Runs the participant pipeline. Deliberately cannot pay or edit bank details — whoever approves an invoice must not also be able to redirect its money.',
  finance: 'Payments, bank details and reconciliation. Deliberately cannot approve the invoices they will later pay.',
  read_only: 'Can see the module and change nothing.',
};
const FN_LABEL = {
  view: 'View', enter: 'Enter', approve: 'Approve', claim: 'Claim', reconcile: 'Reconcile',
  pay: 'Pay', bank_details: 'Bank details', plan_funding: 'Plan funding', ndia_connection: 'NDIA connection',
};
const roleLabel = (r) => ROLE_LABEL[r] || pretty(r);
const fnLabel = (f) => FN_LABEL[f] || pretty(f);

function RoleMatrix({ functions, matrix }) {
  const roles = useMemo(() => {
    const keys = Object.keys(matrix || {});
    return [...ROLE_ORDER.filter((r) => keys.includes(r)), ...keys.filter((r) => !ROLE_ORDER.includes(r))];
  }, [matrix]);
  if (!roles.length) return <EmptyState icon={ShieldCheck} title="No roles defined" hint="The API returned no permission matrix." />;
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-separate border-spacing-0 text-[13px]">
        <caption className="sr-only">Plan-management permissions by role and function</caption>
        <thead>
          <tr>
            <th scope="col" className="sticky left-0 z-10 bg-white border-b border-r border-slate-200 text-left w-[290px] min-w-[240px] px-4 py-2">
              <MicroLabel>Role</MicroLabel>
            </th>
            {functions.map((f) => (
              <th key={f} scope="col" className="border-b border-slate-200 px-2 py-2 text-center align-bottom">
                <span className="block text-[10.5px] font-semibold uppercase tracking-[0.05em] text-slate-400 whitespace-nowrap">{fnLabel(f)}</span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {roles.map((r) => {
            const has = new Set(matrix[r] || []);
            return (
              <tr key={r} className="group">
                <th scope="row" className="sticky left-0 z-10 bg-white group-hover:bg-slate-50 border-b border-r border-slate-200 px-4 py-2.5 text-left font-normal align-top">
                  <span className="block text-[13px] font-semibold text-slate-800">{roleLabel(r)}</span>
                  <span className="block text-[11.5px] text-slate-500 mt-0.5">{ROLE_BLURB[r] || ''}</span>
                  <span className="block text-[10.5px] tabular-nums text-slate-400 mt-1">{has.size} of {functions.length} functions</span>
                </th>
                {functions.map((f) => (
                  <td key={f} className="border-b border-slate-100 px-2 py-2.5 text-center group-hover:bg-slate-50/60">
                    {has.has(f) ? (
                      <span title={`${roleLabel(r)} can ${fnLabel(f).toLowerCase()}`}
                        className="inline-flex w-5 h-5 items-center justify-center rounded-md bg-emerald-50 text-emerald-600 ring-1 ring-inset ring-emerald-200">
                        <Check size={12} strokeWidth={3} />
                      </span>
                    ) : (
                      // The gaps are the reason this grid exists, so they get a real mark
                      // rather than whitespace that reads as "not loaded yet".
                      <span title={`${roleLabel(r)} cannot ${fnLabel(f).toLowerCase()}`} className="inline-block w-5 h-5 leading-5 text-slate-200 select-none">–</span>
                    )}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function RoleRegister() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [nonce, setNonce] = useState(0);
  const reload = () => setNonce((n) => n + 1);
  const [savingId, setSavingId] = useState(null);
  const [denied, setDenied] = useState(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    let alive = true;
    setLoading(true); setError(null);
    api.get('/plan-management/roles')
      .then((r) => { if (alive) setData(r.data); })
      .catch((e) => { if (alive) setError(errMsg(e, 'Could not load plan-management roles.')); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [nonce]);

  const functions = useMemo(() => (data && data.functions) || [], [data]);
  const matrix = useMemo(() => (data && data.matrix) || {}, [data]);
  const users = useMemo(() => (data && data.users) || [], [data]);
  const roleOptions = useMemo(() => {
    const keys = Object.keys(matrix);
    return [...ROLE_ORDER.filter((r) => keys.includes(r)), ...keys.filter((r) => !ROLE_ORDER.includes(r))];
  }, [matrix]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) => `${u.name || ''} ${u.email || ''} ${u.pm_role || ''}`.toLowerCase().includes(q));
  }, [users, search]);

  const optedIn = users.filter((u) => u.opted_in).length;

  const changeRole = async (u, value) => {
    const pmRole = value === '' ? null : value;
    setSavingId(u.id);
    try {
      const { data: updated } = await api.put(`/plan-management/roles/${u.id}`, { pm_role: pmRole });
      setData((d) => (d ? {
        ...d,
        users: (d.users || []).map((x) => (x.id === u.id
          ? { ...x, pm_role: updated.pm_role, effective: updated.effective || [], opted_in: !!updated.pm_role }
          : x)),
      } : d));
      setDenied(null);
      toast.success(pmRole
        ? `${u.name} is now ${roleLabel(pmRole)} in plan management`
        : `${u.name} keeps the access they have today`);
    } catch (e) {
      const msg = errMsg(e, 'Could not change that role.');
      // A 403 here is the API telling us only administrators hand out permissions — show its
      // words, not ours, and stop offering an action that cannot succeed.
      if (httpStatus(e) === 403) setDenied(msg);
      toast.error(msg);
    } finally { setSavingId(null); }
  };

  return (
    <div className="space-y-4">
      {denied && (
        <div className="rounded-xl border border-amber-200 bg-amber-50/70 px-4 py-3 flex items-start gap-2.5">
          <Lock size={14} className="text-amber-600 shrink-0 mt-0.5" />
          <p className="text-[12.5px] text-slate-700"><span className="font-semibold text-amber-700">Read only here.</span> {denied}</p>
        </div>
      )}

      <SectionCard title="Permissions by function" icon={ShieldCheck}
        actions={<span className="text-[11.5px] text-slate-400 tabular-nums">{Object.keys(matrix).length} roles · {functions.length} functions</span>}>
        {loading ? <TableSkeleton rows={4} cols={6} />
          : error ? <ErrorState message={error} onRetry={reload} />
            : <RoleMatrix functions={functions} matrix={matrix} />}
      </SectionCard>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <Toolbar
          sticky={false} title="Who has which role" icon={Users}
          count={loading ? undefined : rows.length} countLabel={rows.length === 1 ? 'user' : 'users'}
          search={search} onSearch={setSearch} searchPlaceholder="Name or email"
          actions={(
            <>
              <ExportButton filename={`pm-roles-${new Date().toISOString().slice(0, 10)}.csv`} rows={rows}
                columns={[
                  { header: 'Name', value: 'name' },
                  { header: 'Email', value: 'email' },
                  { header: 'App role', value: 'role' },
                  { header: 'PM role', value: 'pm_role' },
                ]} />
              <button type="button" onClick={reload} className="btn-secondary btn-sm" disabled={loading}>
                <RefreshCw size={12} className={clsx(loading && 'animate-spin')} />Refresh
              </button>
            </>
          )}
        />
        <DataTable
          dense maxHeight="32rem" label="Plan-management roles by user"
          rows={rows} loading={loading} error={error} onRetry={reload}
          empty={{
            icon: Users,
            title: search ? 'No user matches that search' : 'No users to show',
            hint: search ? 'Clear the search to see everyone.' : 'Admins, office users and anyone already holding a plan-management role appear here.',
          }}
          columns={[
            {
              key: 'name',
              header: 'User',
              sortable: true,
              render: (u) => (
                <span className="min-w-0 block">
                  <span className="block truncate text-slate-800">{u.name}</span>
                  <span className="block truncate text-[11px] text-slate-400">{u.email}</span>
                </span>
              ),
            },
            { key: 'role', header: 'System role', width: 130, sortable: true, render: (u) => <span className="badge badge-gray !text-[10.5px]">{pretty(u.role)}</span> },
            {
              key: 'pm_role',
              header: 'Plan-management role',
              width: 200,
              sortable: true,
              render: (u) => (
                <select
                  value={u.pm_role || ''} disabled={savingId === u.id || !!denied}
                  aria-label={`Plan-management role for ${u.name}`}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => changeRole(u, e.target.value)}
                  className={clsx(CTRL, 'w-full')}
                >
                  <option value="">No plan-management role</option>
                  {roleOptions.map((r) => <option key={r} value={r}>{roleLabel(r)}</option>)}
                </select>
              ),
            },
            {
              key: 'effective',
              header: 'Effective access',
              render: (u) => (u.opted_in ? (
                <span className="flex flex-wrap items-center gap-1">
                  {(u.effective || []).map((f) => (
                    <span key={f} className="rounded px-1.5 py-0.5 text-[10.5px] font-medium bg-slate-100 text-slate-600">{fnLabel(f)}</span>
                  ))}
                </span>
              ) : (
                // Not opted in is the DESIGNED default, not a gap: a null pm_role behaves exactly
                // as it did before the roles existed. Neutral slate, never a warning colour.
                <span className="inline-flex items-center gap-1.5 text-[11.5px] text-slate-500">
                  <span className="w-1.5 h-1.5 rounded-full bg-slate-300 shrink-0" aria-hidden />
                  Existing access — not yet restricted
                </span>
              )),
            },
          ]}
          footer={users.length ? `${optedIn} of ${users.length} user${users.length === 1 ? '' : 's'} have a plan-management role. The rest keep the access they have today.` : undefined}
        />
        {data && data.note ? <div className="px-4 py-2.5 border-t border-slate-100"><NoteLine>{data.note}</NoteLine></div> : null}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   Shell
   ══════════════════════════════════════════════════════════════════════════ */

const TABS = [
  { key: 'fees', label: 'Fees', icon: Grid3x3, Panel: FeeRegister },
  { key: 'statements', label: 'Statements', icon: Mail, Panel: StatementRegister },
  { key: 'batches', label: 'Payment batches', icon: Landmark, Panel: PaymentBatchRegister },
  { key: 'roles', label: 'Roles', icon: ShieldCheck, Panel: RoleRegister },
];

// Controlled when `tab`/`onTabChange` are supplied (so the page can route ?tab=), uncontrolled
// otherwise. Only the open panel mounts, so nothing fetches until it is looked at.
export default function Registers({ tab, onTabChange }) {
  const [inner, setInner] = useState(TABS[0].key);
  const active = TABS.some((t) => t.key === tab) ? tab : inner;
  const pick = (k) => { setInner(k); if (onTabChange) onTabChange(k); };
  const Panel = (TABS.find((t) => t.key === active) || TABS[0]).Panel;

  return (
    <div className="space-y-4">
      <div role="tablist" aria-label="Registers" className="inline-flex items-center gap-1 p-1 bg-slate-100 rounded-xl">
        {TABS.map((t) => {
          const on = t.key === active;
          return (
            <button
              key={t.key} type="button" role="tab" id={`pm-register-tab-${t.key}`}
              aria-selected={on} aria-controls={`pm-register-panel-${t.key}`}
              // Roving tabindex: only the open tab is a tab stop, so Tab moves past the whole
              // strip rather than through four buttons.
              tabIndex={on ? 0 : -1} onClick={() => pick(t.key)}
              className={clsx('inline-flex items-center gap-1.5 h-8 px-3 rounded-lg text-[12.5px] font-medium whitespace-nowrap',
                'transition-colors motion-reduce:transition-none focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400',
                on ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-800')}
            >
              <t.icon size={14} className={clsx('shrink-0', on ? 'text-primary-500' : 'text-slate-400')} />
              {t.label}
            </button>
          );
        })}
      </div>
      <div role="tabpanel" id={`pm-register-panel-${active}`} aria-labelledby={`pm-register-tab-${active}`}>
        {/* Keyed so switching registers resets panel state instead of leaking one panel's
            filters into the next. Only the open panel mounts, so nothing fetches unseen. */}
        <Panel key={active} />
      </div>
    </div>
  );
}
